/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { create } from 'zustand'
import { smartChatCompletion, Message, Provider, Tool, getMessageText } from '@/providers/llm-provider'
import { search, getWebSearchTool, hasSearchApiKey } from '@/providers/search-api'
import { getApiKey } from '@/lib/api-keys'
import { getGitTools, type GitToolAction } from '@/providers/git-tools'
import { getFileTools, type FileToolAction } from '@/providers/file-tools'
import { getMemoryTool } from '@/providers/memory-tool'
import { useLogsStore } from './logs-store'

export interface ChatMessage extends Message {
  id: string
  timestamp: number
  isStreaming?: boolean
  attachedFiles?: Array<{ path: string; content: string; type?: 'text' | 'image'; mimeType?: string }>
  model?: string // Track which model generated this message
}

export type OrchestratorMode = 'brainstorm' | 'peer-review'

interface ChatState {
  messages: ChatMessage[]
  isLoading: boolean
  error: string | null
  provider: Provider
  model: string
  onResponseComplete?: (message: string) => void
  onSaveCompiledDocument?: (document: string) => Promise<void>
  abortController: AbortController | null
  mode: OrchestratorMode
  showHandoverModal: boolean
  compiledDocument: string
  webSearchEnabled: boolean // Toggle for web search in orchestrator
  gitToolsEnabled: boolean // Toggle for git commit/push tools
  fileToolsEnabled: boolean // Toggle for file operation tools
  pendingGitAction: GitToolAction | null // Pending git action awaiting confirmation
  pendingGitToolCall: { id: string; args: any } | null // Store the tool call ID and args for sending result back to LLM
  pendingFileAction: FileToolAction | null // Pending file action awaiting confirmation
  pendingFileToolCall: { id: string; args: any } | null // Store the file tool call ID and args
  pendingMemoryToolCall: { id: string; args: any } | null // Store the memory tool call ID and args

  // Cost tracking
  sessionCost: number // Total cost for this chat session
  sessionTokens: number // Total tokens for this chat session
  lastRequestCost: number // Cost of last API call
  lastRequestTokens: number // Tokens of last API call
  lastRequestLatency: number // Latency of last API call in ms

  // Actions
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => void
  sendMessage: (content: string, attachedFiles?: Array<{ path: string; content: string }>) => Promise<void>
  stopStreaming: () => void
  clearMessages: () => void
  setProvider: (provider: Provider) => void
  setModel: (model: string) => void
  updateStreamingMessage: (id: string, content: string) => void
  setOnResponseComplete: (callback?: (message: string) => void) => void
  setOnSaveCompiledDocument: (callback?: (document: string) => Promise<void>) => void
  loadMessages: (messages: ChatMessage[]) => void
  resetToDefault: () => void
  setMode: (mode: OrchestratorMode) => void
  compileProjectDocument: () => Promise<void>
  setCompiledDocument: (document: string) => void
  closeHandoverModal: () => void
  approveHandover: () => Promise<void>
  brainstormMore: (feedback: string) => void
  setWebSearchEnabled: (enabled: boolean) => void
  setGitToolsEnabled: (enabled: boolean) => void
  setFileToolsEnabled: (enabled: boolean) => void
  setPendingGitAction: (action: GitToolAction | null) => void
  confirmGitAction: () => Promise<void>
  cancelGitAction: () => void
  sendGitToolResult: (success: boolean, message: string) => Promise<void>
  setPendingFileAction: (action: FileToolAction | null) => void
  confirmFileAction: () => Promise<void>
  cancelFileAction: () => void
  sendFileToolResult: (success: boolean, message: string) => Promise<void>
  sendMemoryToolResult: (success: boolean, message: string) => Promise<void>
  resetCostTracking: () => void
}

// System prompts for different modes
const BRAINSTORM_SYSTEM_PROMPT = `You are the Hivemind Orchestrator in Brainstorm mode.

Your role in Brainstorm mode:
- Engage in natural, conversational dialogue with the user
- Help explore ideas, refine concepts, and make decisions
- Ask clarifying questions when needed
- Provide thoughtful analysis and suggestions
- Build context organically through discussion
- Respond in clear, natural language (NOT MOTH format)

IMPORTANT - Tool Usage:
- You have access to tools for file operations (create_file, update_file, delete_file, rename_file) and git operations (commit_and_push, create_github_repo)
- When the user asks you to create, modify, or delete files, USE the file operation tools
- When the user asks you to commit changes, push to GitHub, or create a repository, USE the git tools
- CRITICAL: If the user says anything like "create a repo", "make a repository", "commit this", "push to GitHub", you MUST call the appropriate git tool - do NOT just describe what you would do
- Don't just describe what you would do - actually call the appropriate tool
- The user will be asked to confirm before any tool action is executed

When the user is ready, they can switch to Peer Review mode to get structured multi-LLM feedback on the conclusions reached.

Be helpful, insightful, and conversational. Use your tools proactively when appropriate.`

const BRAINSTORM_WEB_SEARCH_PROMPT = `You are the Hivemind Orchestrator in Brainstorm mode with WEB SEARCH ENABLED.

Your role in Brainstorm mode:
- Engage in natural, conversational dialogue with the user
- Help explore ideas, refine concepts, and make decisions
- Ask clarifying questions when needed
- Provide thoughtful analysis and suggestions
- Build context organically through discussion
- Respond in clear, natural language (NOT MOTH format)

WEB SEARCH CAPABILITIES:
- You have access to real-time web search and current information
- Use web search to find up-to-date facts, news, research, and data
- Cite sources when providing information from the web
- Verify information across multiple sources when possible
- Ideal for journalism, research, fact-checking, and current events

IMPORTANT - Tool Usage:
- You have access to tools for file operations (create_file, update_file, delete_file, rename_file) and git operations (commit_and_push, create_github_repo)
- When the user asks you to create, modify, or delete files, USE the file operation tools
- When the user asks you to commit changes, push to GitHub, or create a repository, USE the git tools
- CRITICAL: If the user says anything like "create a repo", "make a repository", "commit this", "push to GitHub", you MUST call the appropriate git tool - do NOT just describe what you would do
- Don't just describe what you would do - actually call the appropriate tool
- The user will be asked to confirm before any tool action is executed

When the user is ready, they can switch to Peer Review mode to get structured multi-LLM feedback on the conclusions reached.

Be helpful, insightful, and conversational. Use your tools proactively when appropriate.`

const PEER_REVIEW_SYSTEM_PROMPT = `You are the Hivemind Orchestrator coordinating multi-LLM creative work.

CRITICAL FORMATTING RULES:
- Respond in MOTH format ONLY
- NO markdown syntax (no **, no ##, no \`\`\`, no _)
- NO prose outside MOTH sections
- Use plain text only
- Use colons and semicolons for structure
- Use hyphens for lists only

Your role:
1. Receive user requests and draft content
2. Coordinate peer reviews (Gemini, Claude, Grok)
3. Synthesize feedback and provide guidance
4. Help iterate on work until quality standards are met

Required MOTH structure for all responses:

[ORCHESTRATOR_RESPONSE]
packet_type:ORCHESTRATOR_RESPONSE
timestamp:<ISO timestamp>

[ANALYSIS]
request_type:<draft|revision|question|feedback>
complexity:<low|medium|high>
recommended_action:<what to do next>

[GUIDANCE]
<your analysis and recommendations in plain text>
key_points:
- point 1 in plain text
- point 2 in plain text

[NEXT_STEPS]
priority:high; action:<what user should do>
priority:medium; action:<optional improvement>

[STATUS]
ready_for_peers:<yes|no>
ready_for_judge:<yes|no>

FORBIDDEN:
- Do NOT use ** for bold
- Do NOT use ## for headers
- Do NOT use \`\`\` for code blocks
- Do NOT use _ for italics
- Do NOT use markdown syntax of any kind

MOTH format uses ONLY:
- [SECTION_NAME] for headers
- key:value for properties
- - hyphen for list items
- Plain text for content

When user sends content, analyze it and provide structured guidance in pure MOTH format.`

// Load saved orchestrator model from localStorage
function loadSavedModel(): string {
  try {
    const saved = localStorage.getItem('orchestratorModel')
    if (saved) {
      console.log('[Chat] Loading saved model from localStorage:', saved)
      return saved
    }

    // No saved model, check available keys to pick a smart default
    if (getApiKey('anthropic')) return 'anthropic/claude-sonnet-4.5'
    if (getApiKey('google')) return 'google/gemini-2.0-flash-exp'
    if (getApiKey('openai')) return 'openai/gpt-4o'
    if (getApiKey('openrouter')) return 'anthropic/claude-sonnet-4.5' // Fallback to Claude via OpenRouter

    // Default fallback
    return 'anthropic/claude-sonnet-4.5'
  } catch (error) {
    console.error('[Chat] Failed to load saved model:', error)
    return 'anthropic/claude-sonnet-4.5'
  }
}

export const useChatStore = create<ChatState>((set, get) => ({
  messages: [
    {
      id: 'system-1',
      role: 'system',
      content: BRAINSTORM_SYSTEM_PROMPT,
      timestamp: Date.now(),
    },
  ],
  isLoading: false,
  error: null,
  provider: 'openrouter',
  model: loadSavedModel(),
  abortController: null,
  mode: 'brainstorm',
  showHandoverModal: false,
  compiledDocument: '',
  webSearchEnabled: true,
  gitToolsEnabled: true,
  fileToolsEnabled: true,
  pendingGitAction: null,
  pendingGitToolCall: null,
  pendingFileAction: null,
  pendingFileToolCall: null,
  pendingMemoryToolCall: null,

  // Cost tracking initialization
  sessionCost: 0,
  sessionTokens: 0,
  lastRequestCost: 0,
  lastRequestTokens: 0,
  lastRequestLatency: 0,

  addMessage: (message) => {
    const newMessage: ChatMessage = {
      ...message,
      id: `msg-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
      timestamp: Date.now(),
    }
    set((state) => ({
      messages: [...state.messages, newMessage],
    }))
  },

  sendMessage: async (content: string, attachedFiles?: Array<{ path: string; content: string; type?: 'text' | 'image'; mimeType?: string }>) => {
    const { messages, model, updateStreamingMessage, mode } = get()
    const startTime = Date.now()

    // Log current mode and system prompt for debugging
    const systemMessage = messages.find(m => m.role === 'system')
    const systemContent = typeof systemMessage?.content === 'string' ? systemMessage.content : ''
    console.log('[Chat] sendMessage called:', {
      mode,
      systemPromptPreview: getMessageText(systemContent).substring(0, 100) + '...',
      isBrainstormPrompt: getMessageText(systemContent).includes('Brainstorm mode'),
      isPeerReviewPrompt: getMessageText(systemContent).includes('MOTH format ONLY'),
      messageContent: content.substring(0, 100)
    })

    // Log what files are being sent
    if (attachedFiles && attachedFiles.length > 0) {
      console.log(`[Chat] Sending message with ${attachedFiles.length} attached file(s):`)
      attachedFiles.forEach((file, idx) => {
        const fileType = file.type || 'text'
        const size = fileType === 'image' ? `${(file.content.length / 1024).toFixed(1)}KB` : `${file.content.length} chars`
        console.log(`  ${idx + 1}. ${file.path} (${fileType}, ${size})`)
      })
    }

    // Create abort controller for this request
    const controller = new AbortController()
    set({ abortController: controller })

    // Add user message
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      timestamp: Date.now(),
      attachedFiles,
    }
    set((state) => ({
      messages: [...state.messages, userMessage],
      isLoading: true,
      error: null,
    }))

    // Create assistant message placeholder for streaming
    const assistantId = `assistant-${Date.now()}`
    const assistantMessage: ChatMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: Date.now(),
      isStreaming: true,
      model, // Track which model is generating this response
    }
    set((state) => ({
      messages: [...state.messages, assistantMessage],
    }))

    try {
      // Prepare messages for API (exclude system messages and IDs)
      console.log(`[Chat] Preparing ${messages.length} messages from history for API...`)
      const apiMessages: Message[] = messages
        .filter((m) => m.role !== 'system' || getMessageText(m.content).includes('Orchestrator'))
        .map((m, idx) => {
          // Include attached files in the message content for API
          let messageContent = m.content
          if (m.attachedFiles && m.attachedFiles.length > 0) {
            console.log(`[Chat] Message ${idx + 1} has ${m.attachedFiles.length} attached file(s) from history:`)
            m.attachedFiles.forEach((file, fileIdx) => {
              console.log(`  ${fileIdx + 1}. ${file.path} (${file.content.length} chars, ~${Math.ceil(file.content.length / 4)} tokens)`)
            })
            const filesContent = m.attachedFiles
              .map(file => `[File: ${file.path}]\n\`\`\`\n${file.content}\n\`\`\``)
              .join('\n\n')
            messageContent = `${messageContent}\n\n${filesContent}`
          }
          return {
            role: m.role,
            content: messageContent,
          }
        })

      // Add the new user message with attached files
      // Check if we have images - if so, use vision format
      const hasImages = attachedFiles?.some(f => f.type === 'image')

      if (hasImages && attachedFiles) {
        // Vision API format with images
        const contentParts: Array<{ type: 'text' | 'image_url'; text?: string; image_url?: { url: string } }> = []

        // Add text content first
        if (content.trim()) {
          contentParts.push({ type: 'text', text: content })
        }

        // Add all files
        for (const file of attachedFiles) {
          if (file.type === 'image') {
            contentParts.push({
              type: 'image_url',
              image_url: { url: file.content } // base64 data URL
            })
          } else {
            // Text file - add as text block
            contentParts.push({
              type: 'text',
              text: `[File: ${file.path}]\n\`\`\`\n${file.content}\n\`\`\``
            })
          }
        }

        apiMessages.push({
          role: 'user',
          content: contentParts
        })
      } else {
        // Standard text format
        let userContent = content
        if (attachedFiles && attachedFiles.length > 0) {
          const filesContent = attachedFiles
            .map(file => `[File: ${file.path}]\n\`\`\`\n${file.content}\n\`\`\``)
            .join('\n\n')
          userContent = `${userContent}\n\n${filesContent}`
        }

        apiMessages.push({
          role: 'user',
          content: userContent,
        })
      }

      // Log total message size being sent to API
      const totalApiChars = apiMessages.reduce((sum, m) => sum + m.content.length, 0)
      const totalApiTokens = Math.ceil(totalApiChars / 4)
      console.log(`[Chat] Sending to API: ${totalApiChars} chars, ~${totalApiTokens} tokens across ${apiMessages.length} messages`)

      // Prepare tools if web search or git tools are enabled
      const { webSearchEnabled, gitToolsEnabled, fileToolsEnabled } = get()
      const tools: Tool[] = []

      // Add web search tool if enabled
      if (webSearchEnabled) {
        tools.push(getWebSearchTool())
      }

      // Add git tools if enabled
      if (gitToolsEnabled) {
        tools.push(...getGitTools())
      }

      // Add file tools if enabled
      if (fileToolsEnabled) {
        tools.push(...getFileTools())
      }

      // Always add memory tool (no confirmation needed)
      tools.push(getMemoryTool())

      // Check if we have search API keys
      const searchApiKeys = {
        tavily: getApiKey('tavily'),
        brave: getApiKey('brave'),
        serper: getApiKey('serper'),
      }
      const hasSearchApi = hasSearchApiKey(searchApiKeys)

      console.log('[Chat] Search API keys check:', {
        tavily: searchApiKeys.tavily ? `${searchApiKeys.tavily.substring(0, 10)}...` : 'NOT SET',
        brave: searchApiKeys.brave ? `${searchApiKeys.brave.substring(0, 10)}...` : 'NOT SET',
        serper: searchApiKeys.serper ? `${searchApiKeys.serper.substring(0, 10)}...` : 'NOT SET',
        hasSearchApi,
      })

      if (webSearchEnabled && !hasSearchApi) {
        console.warn('[Chat] Web search enabled but no search API keys configured')
      }

      console.log('[Chat] Tool calling config:', {
        webSearchEnabled,
        gitToolsEnabled,
        hasSearchApi,
        toolsProvided: tools.length > 0,
        toolCount: tools.length,
      })

      if (tools.length > 0) {
        console.log('[Chat] Tools being sent to API:', tools.map(t => t.function.name))
        console.log('[Chat] Full tool definitions:', JSON.stringify(tools, null, 2))
      }

      // Detect if user is requesting a git/file operation (simple keyword detection)
      const userMessage = content.toLowerCase()
      const isGitRequest = /\b(create|make|setup|init).*(repo|repository)|commit|push|pull/i.test(userMessage)
      const isFileRequest = /\b(create|write|update|modify|delete|rename).*(file|folder|directory)/i.test(userMessage)

      // Determine tool_choice based on request type
      let toolChoice: 'auto' | 'required' = 'auto'
      if ((isGitRequest && gitToolsEnabled) || (isFileRequest && fileToolsEnabled)) {
        toolChoice = 'required'
        console.log('[Chat] Git/file operation detected - forcing tool use with tool_choice: required')
      }

      // Call LLM API with streaming
      // Note: Tool calling doesn't work well with streaming, so we'll use non-streaming for now
      if (tools.length > 0 && (hasSearchApi || gitToolsEnabled || fileToolsEnabled)) {
        // Non-streaming with tool calling
        console.log('[Chat] Calling API with tools enabled, toolChoice:', toolChoice)
        const response = await smartChatCompletion({
          model,
          messages: apiMessages,
          temperature: 0.7,
          stream: false,
          tools,
          toolChoice,
        })

        console.log('[Chat] API response:', {
          hasContent: !!response.content,
          hasToolCalls: !!response.toolCalls,
          toolCallCount: response.toolCalls?.length || 0,
          usage: response.usage,
        })

        // Track cost from this API call
        if (response.usage) {
          const requestCost = response.usage.cost || 0
          const requestTokens = response.usage.totalTokens || 0
          const requestLatency = Date.now() - startTime

          set((state) => ({
            sessionCost: state.sessionCost + requestCost,
            sessionTokens: state.sessionTokens + requestTokens,
            lastRequestCost: requestCost,
            lastRequestTokens: requestTokens,
            lastRequestLatency: requestLatency,
          }))

          console.log('[Chat] Cost tracking updated:', {
            requestCost,
            requestTokens,
            requestLatency,
            sessionCost: get().sessionCost,
            sessionTokens: get().sessionTokens,
          })
        }

        // Check if model wants to call tools
        if (response.toolCalls && response.toolCalls.length > 0) {
          console.log('[Chat] Model requested tool calls:', response.toolCalls)

          // Save the tool_calls in the assistant message so we can reference them later
          set((state) => ({
            messages: state.messages.map((m) =>
              m.id === assistantId ? { ...m, tool_calls: response.toolCalls } : m
            ),
          }))

          // Add tool calls to logs
          useLogsStore.getState().addLog({
            type: 'tool_call',
            provider: model.split('/')[0] || 'unknown',
            model: model,
            message: `Requested ${response.toolCalls.length} tool call(s)`,
            toolCalls: response.toolCalls.map(tc => ({
              name: tc.function.name,
              args: JSON.parse(tc.function.arguments)
            }))
          })

          // Execute tool calls
          for (const toolCall of response.toolCalls) {
            if (toolCall.function.name === 'web_search') {
              try {
                const args = JSON.parse(toolCall.function.arguments)
                console.log('[Chat] Executing web search:', args)

                // Update UI to show search is happening
                updateStreamingMessage(assistantId, `🔍 Searching the web for: "${args.query}"...\n\n`)

                const results = await search(args.query, searchApiKeys, {
                  numResults: args.num_results || 5,
                })

                console.log('[Chat] Search results:', results)

                // Format results for display
                const resultsText = results
                  .map((r, i) => `${i + 1}. **${r.title}**\n   ${r.snippet}\n   Source: ${r.url}`)
                  .join('\n\n')

                updateStreamingMessage(assistantId, `🔍 Searched the web for: "${args.query}"\n\nFound ${results.length} results:\n\n${resultsText}\n\n---\n\n`)

                // Add tool response to messages
                apiMessages.push({
                  role: 'assistant',
                  content: response.content || '',
                  tool_calls: response.toolCalls,
                })

                apiMessages.push({
                  role: 'tool',
                  content: JSON.stringify(results),
                  tool_call_id: toolCall.id,
                })

                // Call model again with tool results
                await smartChatCompletion({
                  model,
                  messages: apiMessages,
                  temperature: 0.7,
                  stream: true,
                  onChunk: (chunk) => {
                    if (controller.signal.aborted) {
                      throw new Error('Streaming stopped by user')
                    }
                    // Append to existing content
                    const currentMessage = get().messages.find(m => m.id === assistantId)
                    const currentContent = typeof currentMessage?.content === 'string' ? currentMessage.content : ''
                    set((state) => ({
                      messages: state.messages.map((m) =>
                        m.id === assistantId
                          ? { ...m, content: currentContent + chunk }
                          : m
                      ),
                    }))
                  },
                })
              } catch (error) {
                console.error('[Chat] Tool execution error:', error)
                updateStreamingMessage(assistantId, `❌ Search failed: ${(error as Error).message}\n\n`)
              }
            } else if (toolCall.function.name === 'commit_and_push') {
              try {
                const args = JSON.parse(toolCall.function.arguments)
                console.log('[Chat] Git tool requested commit_and_push:', args)

                updateStreamingMessage(assistantId, `---\n\n## 🔧 Git Commit & Push\n\n**Commit Message:** "${args.message}"\n\n**Status:** ⏳ Waiting for your approval...\n\n*A confirmation modal will appear shortly. Please review and approve the commit.*\n\n---\n\n`)

                // Store the tool call ID and args for later
                // App.tsx will handle the confirmation and execution
                set({
                  pendingGitToolCall: {
                    id: toolCall.id,
                    args
                  }
                })

              } catch (error) {
                console.error('[Chat] Git tool error:', error)
                updateStreamingMessage(assistantId, `❌ **Git operation failed:** ${(error as Error).message}\n\n`)
              }
            } else if (toolCall.function.name === 'create_github_repo') {
              try {
                const args = JSON.parse(toolCall.function.arguments)
                console.log('[Chat] Git tool requested create_github_repo:', args)

                updateStreamingMessage(assistantId, `---\n\n## 🔧 Create GitHub Repository\n\n**Repository:** \`${args.name}\`\n\n**Description:** ${args.description || 'None'}\n\n**Private:** ${args.private ? 'Yes' : 'No'}\n\n**Status:** ⏳ Waiting for your approval...\n\n*A confirmation modal will appear shortly. Please review and approve the repository creation.*\n\n---\n\n`)

                // Store the tool call ID and args for later
                set({
                  pendingGitToolCall: {
                    id: toolCall.id,
                    args
                  }
                })

              } catch (error) {
                console.error('[Chat] Git tool error:', error)
                updateStreamingMessage(assistantId, `❌ **Repository creation failed:** ${(error as Error).message}\n\n`)
              }
            } else if (toolCall.function.name === 'create_file') {
              try {
                const args = JSON.parse(toolCall.function.arguments)
                console.log('[Chat] File tool requested create_file:', args)

                updateStreamingMessage(assistantId, `---\n\n## 📝 Creating File\n\n**File:** \`${args.path}\`\n\n**Status:** ⏳ Waiting for your approval...\n\n*A confirmation modal will appear shortly. Please review the file content and approve the creation.*\n\n---\n\n`)

                set({
                  pendingFileToolCall: {
                    id: toolCall.id,
                    args
                  }
                })

              } catch (error) {
                console.error('[Chat] File tool error:', error)
                updateStreamingMessage(assistantId, `❌ **File operation failed:** ${(error as Error).message}\n\n`)
              }
            } else if (toolCall.function.name === 'update_file') {
              try {
                const args = JSON.parse(toolCall.function.arguments)
                console.log('[Chat] File tool requested update_file:', args)

                updateStreamingMessage(assistantId, `---\n\n## ✏️ Updating File\n\n**File:** \`${args.path}\`\n\n**Status:** ⏳ Waiting for your approval...\n\n*A confirmation modal will appear shortly showing the changes. Please review and approve.*\n\n---\n\n`)

                set({
                  pendingFileToolCall: {
                    id: toolCall.id,
                    args
                  }
                })

              } catch (error) {
                console.error('[Chat] File tool error:', error)
                updateStreamingMessage(assistantId, `❌ **File operation failed:** ${(error as Error).message}\n\n`)
              }
            } else if (toolCall.function.name === 'delete_file') {
              try {
                const args = JSON.parse(toolCall.function.arguments)
                console.log('[Chat] File tool requested delete_file:', args)

                updateStreamingMessage(assistantId, `---\n\n## 🗑️ Deleting File\n\n**File:** \`${args.path}\`\n\n**Status:** ⏳ Waiting for your approval...\n\n*A confirmation modal will appear shortly. Please review and approve the deletion.*\n\n---\n\n`)

                set({
                  pendingFileToolCall: {
                    id: toolCall.id,
                    args
                  }
                })

              } catch (error) {
                console.error('[Chat] File tool error:', error)
                updateStreamingMessage(assistantId, `❌ **File operation failed:** ${(error as Error).message}\n\n`)
              }
            } else if (toolCall.function.name === 'rename_file') {
              try {
                const args = JSON.parse(toolCall.function.arguments)
                console.log('[Chat] File tool requested rename_file:', args)

                updateStreamingMessage(assistantId, `---\n\n## 📝 Renaming File\n\n**From:** \`${args.old_path}\`\n\n**To:** \`${args.new_path}\`\n\n**Status:** ⏳ Waiting for your approval...\n\n*A confirmation modal will appear shortly. Please review and approve the rename.*\n\n---\n\n`)

                set({
                  pendingFileToolCall: {
                    id: toolCall.id,
                    args
                  }
                })

              } catch (error) {
                console.error('[Chat] File tool error:', error)
                updateStreamingMessage(assistantId, `❌ **File operation failed:** ${(error as Error).message}\n\n`)
              }
            } else if (toolCall.function.name === 'remember') {
              try {
                const args = JSON.parse(toolCall.function.arguments)
                console.log('[Chat] Memory tool requested remember:', args)

                // Memory tool doesn't need confirmation - execute immediately
                // This will be handled by App.tsx which has access to updateFile
                updateStreamingMessage(assistantId, `---\n\n## 🧠 Storing Memory\n\n**Memory:** ${args.memory}\n\n**Category:** ${args.category}\n\n**Status:** ⏳ Saving...\n\n---\n\n`)

                // Store the tool call for App.tsx to handle
                set({
                  pendingMemoryToolCall: {
                    id: toolCall.id,
                    args
                  }
                })

              } catch (error) {
                console.error('[Chat] Memory tool error:', error)
                updateStreamingMessage(assistantId, `❌ **Memory storage failed:** ${(error as Error).message}\n\n`)
              }
            }
          }
        } else {
          // No tool calls, just display the response
          updateStreamingMessage(assistantId, response.content)
        }
      } else {
        // Standard streaming without tools
        const streamResponse = await smartChatCompletion({
          model,
          messages: apiMessages,
          temperature: 0.7,
          stream: true,
          onChunk: (chunk) => {
            // Check if aborted
            if (controller.signal.aborted) {
              throw new Error('Streaming stopped by user')
            }
            updateStreamingMessage(assistantId, chunk)
          },
        })

        // Track cost from streaming response (uses estimated values)
        if (streamResponse.usage) {
          const requestCost = streamResponse.usage.cost || 0
          const requestTokens = streamResponse.usage.totalTokens || 0
          const requestLatency = Date.now() - startTime

          set((state) => ({
            sessionCost: state.sessionCost + requestCost,
            sessionTokens: state.sessionTokens + requestTokens,
            lastRequestCost: requestCost,
            lastRequestTokens: requestTokens,
            lastRequestLatency: requestLatency,
          }))

          console.log('[Chat] Cost tracking updated (streaming):', {
            requestCost,
            requestTokens,
            requestLatency,
            sessionCost: get().sessionCost,
            sessionTokens: get().sessionTokens,
          })
        }
      }

      // Mark streaming as complete
      set((state) => ({
        messages: state.messages.map((m) =>
          m.id === assistantId ? { ...m, isStreaming: false } : m
        ),
        isLoading: false,
        abortController: null,
      }))

      // Trigger callback if set (for auto-sending to peers)
      const finalMessage = get().messages.find((m) => m.id === assistantId)
      if (finalMessage && get().onResponseComplete) {
        get().onResponseComplete?.(getMessageText(finalMessage.content))
      }
    } catch (error) {
      const errorMessage = (error as Error).message
      console.error('Chat error:', error)

      set({
        error: errorMessage,
        isLoading: false,
        abortController: null,
      })

      // Remove the failed assistant message placeholder instead of replacing with error
      // The error will be shown in the error modal, not in the chat
      set((state) => ({
        messages: state.messages.filter((m) => m.id !== assistantId),
      }))
    }
  },

  stopStreaming: () => {
    const { abortController } = get()
    if (abortController) {
      abortController.abort()
      set({
        isLoading: false,
        abortController: null,
      })
    }
  },

  updateStreamingMessage: (id: string, chunk: string) => {
    set((state) => ({
      messages: state.messages.map((m) => {
        if (m.id === id) {
          // Handle both string and array content
          const currentContent = typeof m.content === 'string' ? m.content : ''
          return { ...m, content: currentContent + chunk }
        }
        return m
      }),
    }))
  },

  clearMessages: () => {
    const mode = get().mode
    set({
      messages: [
        {
          id: 'system-1',
          role: 'system',
          content: mode === 'brainstorm' ? BRAINSTORM_SYSTEM_PROMPT : PEER_REVIEW_SYSTEM_PROMPT,
          timestamp: Date.now(),
        },
      ],
    })
  },

  setProvider: (provider) => {
    set({ provider })
  },

  setModel: (model) => {
    console.log('[Chat] setModel called with:', model)
    set({ model })
    // Save to localStorage for persistence across refreshes
    try {
      localStorage.setItem('orchestratorModel', model)
      console.log('[Chat] Saved model to localStorage:', model)
    } catch (error) {
      console.error('[Chat] Failed to save model to localStorage:', error)
    }
  },

  setOnResponseComplete: (callback) => {
    set({ onResponseComplete: callback })
  },

  setOnSaveCompiledDocument: (callback) => {
    set({ onSaveCompiledDocument: callback })
  },

  loadMessages: (messages: ChatMessage[]) => {
    const { mode, webSearchEnabled } = get()

    // Determine the correct system prompt
    const getSystemPrompt = () => {
      if (mode === 'brainstorm') {
        return webSearchEnabled ? BRAINSTORM_WEB_SEARCH_PROMPT : BRAINSTORM_SYSTEM_PROMPT
      }
      return PEER_REVIEW_SYSTEM_PROMPT
    }

    // Replace the system message with the current mode's system prompt
    const updatedMessages = messages.map((m, idx) =>
      idx === 0 && m.role === 'system'
        ? { ...m, content: getSystemPrompt() }
        : m
    )
    set({ messages: updatedMessages })
  },

  resetToDefault: () => {
    const savedModel = loadSavedModel()
    console.log('[Chat] resetToDefault called, using saved model:', savedModel)
    set({
      messages: [
        {
          id: 'system-1',
          role: 'system',
          content: BRAINSTORM_SYSTEM_PROMPT,
          timestamp: Date.now(),
        },
      ],
      isLoading: false,
      error: null,
      mode: 'brainstorm',
      webSearchEnabled: true,
      gitToolsEnabled: true,
      fileToolsEnabled: true,
      model: savedModel, // Preserve user's model selection
    })
  },

  setMode: (mode: OrchestratorMode) => {
    const currentMode = get().mode
    const { webSearchEnabled } = get()

    console.log('[Chat] setMode called:', {
      from: currentMode,
      to: mode,
      willCompile: currentMode === 'brainstorm' && mode === 'peer-review'
    })

    // Determine the correct system prompt based on mode and web search
    const getSystemPrompt = (targetMode: OrchestratorMode) => {
      if (targetMode === 'brainstorm') {
        return webSearchEnabled ? BRAINSTORM_WEB_SEARCH_PROMPT : BRAINSTORM_SYSTEM_PROMPT
      }
      return PEER_REVIEW_SYSTEM_PROMPT
    }

    // If switching from brainstorm to peer-review, trigger compilation
    if (currentMode === 'brainstorm' && mode === 'peer-review') {
      // Preemptively switch mode and update the system prompt so the UI and
      // subsequent messages reflect Peer Review rules immediately
      set((state) => ({
        mode: 'peer-review',
        messages: state.messages.map((m, idx) =>
          idx === 0 && m.role === 'system'
            ? { ...m, content: PEER_REVIEW_SYSTEM_PROMPT }
            : m
        ),
      }))

      console.log('[Chat] Preemptively switched to peer-review mode before compilation')

      // Start the handover compilation flow
      get().compileProjectDocument()
    } else {
      // Otherwise just switch the mode and update system prompt
      set((state) => {
        const newMessages = state.messages.map((m, idx) =>
          idx === 0 && m.role === 'system'
            ? { ...m, content: getSystemPrompt(mode) }
            : m
        )
        console.log('[Chat] Mode switched, system prompt updated:', {
          mode,
          webSearchEnabled,
          systemPromptPreview: getMessageText(newMessages[0]?.content || '').substring(0, 100) + '...'
        })
        return {
          mode,
          messages: newMessages,
        }
      })
    }
  },

  setWebSearchEnabled: (enabled: boolean) => {
    const { mode } = get()

    console.log('[Chat] setWebSearchEnabled called:', {
      enabled,
      mode,
      willUpdatePrompt: mode === 'brainstorm'
    })

    // Update web search state
    set({ webSearchEnabled: enabled })

    // If in brainstorm mode, update the system prompt
    if (mode === 'brainstorm') {
      set((state) => ({
        messages: state.messages.map((m, idx) =>
          idx === 0 && m.role === 'system'
            ? { ...m, content: enabled ? BRAINSTORM_WEB_SEARCH_PROMPT : BRAINSTORM_SYSTEM_PROMPT }
            : m
        ),
      }))

      console.log('[Chat] System prompt updated for web search:', {
        enabled,
        promptType: enabled ? 'WEB_SEARCH' : 'STANDARD'
      })
    }
  },

  compileProjectDocument: async () => {
    const { messages, model } = get()

    // Filter out system messages to get the brainstorm conversation
    const conversationMessages = messages.filter(m => m.role !== 'system')

    if (conversationMessages.length === 0) {
      // No conversation to compile, just switch mode
      set((state) => ({
        mode: 'peer-review',
        messages: state.messages.map((m, idx) =>
          idx === 0 && m.role === 'system'
            ? { ...m, content: PEER_REVIEW_SYSTEM_PROMPT }
            : m
        ),
      }))
      return
    }

    // Show loading state
    set({ isLoading: true, showHandoverModal: true })

    try {
      // Prepare messages for compilation
      const compilationPrompt = `Review this brainstorm conversation and write a definitive project document capturing all conclusions, decisions, and agreements we reached. Be specific and comprehensive. Format it clearly with sections and bullet points.

Conversation:
${conversationMessages.map(m => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`).join('\n\n')}`

      let compiledDoc = ''

      // Call LLM to compile the document
      await smartChatCompletion({
        model,
        messages: [
          { role: 'user', content: compilationPrompt }
        ],
        temperature: 0.3,
        stream: true,
        onChunk: (chunk) => {
          compiledDoc += chunk
          set({ compiledDocument: compiledDoc })
        },
      })

      set({
        isLoading: false,
        compiledDocument: compiledDoc,
      })
    } catch (error) {
      console.error('Failed to compile project document:', error)
      set({
        isLoading: false,
        error: (error as Error).message,
        showHandoverModal: false,
      })
    }
  },

  setCompiledDocument: (document: string) => {
    set({ compiledDocument: document })
  },

  closeHandoverModal: () => {
    set({
      showHandoverModal: false,
      compiledDocument: '',
    })
  },

  approveHandover: async () => {
    // Close modal and switch to peer review mode
    const { compiledDocument, onResponseComplete, onSaveCompiledDocument } = get()

    console.log('[Chat Store] approveHandover called:', {
      hasCompiledDocument: !!compiledDocument,
      documentLength: compiledDocument?.length,
      hasOnResponseComplete: !!onResponseComplete,
      hasOnSaveCompiledDocument: !!onSaveCompiledDocument
    })

    // Switch mode and update the system prompt to Peer Review
    set((state) => ({
      mode: 'peer-review',
      showHandoverModal: false,
      messages: state.messages.map((m, idx) =>
        idx === 0 && m.role === 'system'
          ? { ...m, content: PEER_REVIEW_SYSTEM_PROMPT }
          : m
      ),
    }))

    console.log('[Chat Store] Mode switched to peer-review')

    // Save the compiled document to project files
    if (onSaveCompiledDocument) {
      await onSaveCompiledDocument(compiledDocument)
      console.log('[Chat Store] Compiled document saved to project files')
    }

    // Wait for state update to propagate to React components
    // This ensures the mode change is reflected in latestValuesRef before calling onResponseComplete
    await new Promise(resolve => setTimeout(resolve, 50))

    // Directly trigger peer review with the compiled document
    // Skip sending to orchestrator, go straight to peers
    if (onResponseComplete) {
      console.log('[Chat Store] Calling onResponseComplete with compiled document:', {
        documentPreview: compiledDocument.substring(0, 100) + '...'
      })
      onResponseComplete(compiledDocument)
    } else {
      console.error('[Chat Store] ❌ onResponseComplete callback is not set!')
    }
  },

  brainstormMore: (feedback: string) => {
    const { compiledDocument } = get()

    // Close modal, stay in brainstorm mode, and add the compiled doc + feedback to the conversation
    set({
      showHandoverModal: false,
      compiledDocument: '',
    })

    // Add compiled document as assistant message
    const compiledMessage: ChatMessage = {
      id: `assistant-compiled-${Date.now()}`,
      role: 'assistant',
      content: `Here's what I've compiled from our conversation so far:\n\n${compiledDocument}`,
      timestamp: Date.now(),
    }

    set((state) => ({
      messages: [...state.messages, compiledMessage],
    }))

    // Send the feedback back to orchestrator
    if (feedback.trim()) {
      get().sendMessage(feedback)
    }
  },

  resetCostTracking: () => {
    set({
      sessionCost: 0,
      sessionTokens: 0,
      lastRequestCost: 0,
      lastRequestTokens: 0,
      lastRequestLatency: 0,
    })
    console.log('[Chat] Cost tracking reset')
  },

  setGitToolsEnabled: (enabled: boolean) => {
    set({ gitToolsEnabled: enabled })
    console.log('[Chat] Git tools enabled:', enabled)
  },

  setPendingGitAction: (action: GitToolAction | null) => {
    set({ pendingGitAction: action })
    console.log('[Chat] Pending git action:', action)
  },

  confirmGitAction: async () => {
    const { pendingGitAction } = get()
    if (!pendingGitAction) {
      console.warn('[Chat] No pending git action to confirm')
      return
    }

    console.log('[Chat] Git action confirmed, executing...')
    // The actual execution will be handled by App.tsx which has access to project context
    // This just clears the pending action
    set({ pendingGitAction: null })
  },

  cancelGitAction: () => {
    const { pendingGitAction, pendingGitToolCall } = get()
    if (pendingGitAction) {
      console.log('[Chat] Git action cancelled')
      set({ pendingGitAction: null, pendingGitToolCall: null })

      // Send cancellation result back to LLM
      if (pendingGitToolCall) {
        get().sendGitToolResult(false, 'User cancelled the git operation')
      }
    }
  },

  sendGitToolResult: async (success: boolean, message: string) => {
    const { pendingGitToolCall, messages, model, updateStreamingMessage } = get()

    if (!pendingGitToolCall) {
      console.warn('[Chat] No pending git tool call to send result for')
      return
    }

    console.log('[Chat] Sending git tool result back to LLM:', { success, message })

    try {
      // Find the last assistant message (the one that requested the git operation)
      const lastAssistantMessage = [...messages].reverse().find(m => m.role === 'assistant')

      if (!lastAssistantMessage) {
        console.error('[Chat] Could not find last assistant message')
        return
      }

      // Update the assistant message to show the result
      const resultMessage = success
        ? `✅ Git operation successful!\n\n${message}\n\n`
        : `❌ Git operation failed:\n\n${message}\n\n`

      updateStreamingMessage(lastAssistantMessage.id, lastAssistantMessage.content + resultMessage)

      // Prepare messages for the API call, excluding system messages and the last assistant message
      const apiMessages: Message[] = messages
        .filter(m => m.role !== 'system' && m.id !== lastAssistantMessage.id)
        .map((m, idx) => {
          let messageContent = m.content
          if (m.attachedFiles && m.attachedFiles.length > 0) {
            const filesContent = m.attachedFiles
              .map(file => `[File: ${file.path}]\n\`\`\`\n${file.content}\n\`\`\``)
              .join('\n\n')
            messageContent = `${messageContent}\n\n${filesContent}`
          }
          return {
            role: m.role,
            content: messageContent,
          }
        })

      // Add the tool call response (assistant message with tool_calls)
      apiMessages.push({
        role: 'assistant',
        content: lastAssistantMessage.content,
        tool_calls: [{
          id: pendingGitToolCall.id,
          type: 'function',
          function: {
            name: 'commit_and_push',
            arguments: JSON.stringify(pendingGitToolCall.args),
          },
        }],
      })

      apiMessages.push({
        role: 'tool',
        content: JSON.stringify({ success, message }),
        tool_call_id: pendingGitToolCall.id,
      })

      // Clear the pending tool call
      set({ pendingGitToolCall: null })

      // Create a new assistant message for the continuation
      const continuationId = `msg-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
      set((state) => ({
        messages: [...state.messages, {
          id: continuationId,
          role: 'assistant',
          content: '',
          timestamp: Date.now(),
          isStreaming: true,
          model,
        }],
      }))

      // Call the LLM again with the tool result
      const response = await smartChatCompletion({
        model,
        messages: apiMessages,
        temperature: 0.7,
        stream: true,
        onChunk: (chunk) => {
          updateStreamingMessage(continuationId, chunk)
        },
      })

      // Mark as complete
      set((state) => ({
        messages: state.messages.map((m) =>
          m.id === continuationId ? { ...m, isStreaming: false } : m
        ),
      }))

      console.log('[Chat] Git tool result sent successfully, LLM continued conversation')

    } catch (error) {
      console.error('[Chat] Error sending git tool result:', error)
      set({ error: error instanceof Error ? error.message : 'Failed to send git tool result' })
    }
  },

  setFileToolsEnabled: (enabled: boolean) => {
    set({ fileToolsEnabled: enabled })
    console.log('[Chat] File tools enabled:', enabled)
  },

  setPendingFileAction: (action: FileToolAction | null) => {
    set({ pendingFileAction: action })
    console.log('[Chat] Pending file action:', action)
  },

  confirmFileAction: async () => {
    const { pendingFileAction } = get()
    if (!pendingFileAction) {
      console.warn('[Chat] No pending file action to confirm')
      return
    }

    console.log('[Chat] File action confirmed, executing...')
    set({ pendingFileAction: null })
  },

  cancelFileAction: () => {
    const { pendingFileAction, pendingFileToolCall } = get()
    if (pendingFileAction) {
      console.log('[Chat] File action cancelled')
      set({ pendingFileAction: null, pendingFileToolCall: null })

      if (pendingFileToolCall) {
        get().sendFileToolResult(false, 'User cancelled the file operation')
      }
    }
  },

  sendFileToolResult: async (success: boolean, message: string) => {
    const { pendingFileToolCall, messages, model, updateStreamingMessage } = get()

    if (!pendingFileToolCall) {
      console.warn('[Chat] No pending file tool call to send result for')
      return
    }

    console.log('[Chat] Sending file tool result back to LLM:', { success, message })

    try {
      const lastAssistantMessage = [...messages].reverse().find(m => m.role === 'assistant')

      if (!lastAssistantMessage) {
        console.error('[Chat] Could not find last assistant message')
        return
      }

      const resultMessage = success
        ? `✅ File operation successful!\n\n${message}\n\n`
        : `❌ File operation failed:\n\n${message}\n\n`

      updateStreamingMessage(lastAssistantMessage.id, lastAssistantMessage.content + resultMessage)

      // Build API messages, excluding system messages and the last assistant message (we'll add it with tool_calls)
      const apiMessages: Message[] = messages
        .filter(m => m.role !== 'system' && m.id !== lastAssistantMessage.id)
        .map((m, idx) => {
          let messageContent = m.content
          if (m.attachedFiles && m.attachedFiles.length > 0) {
            const filesContent = m.attachedFiles
              .map(file => `[File: ${file.path}]\n\`\`\`\n${file.content}\n\`\`\``)
              .join('\n\n')
            messageContent = `${messageContent}\n\n${filesContent}`
          }
          return {
            role: m.role,
            content: messageContent,
          }
        })

      // Determine the tool name from the args
      let toolName = 'create_file'
      if (pendingFileToolCall.args.old_path) {
        toolName = 'rename_file'
      } else if (pendingFileToolCall.args.content && messages.some(m => getMessageText(m.content).includes('update file'))) {
        toolName = 'update_file'
      } else if (!pendingFileToolCall.args.content && !pendingFileToolCall.args.old_path) {
        toolName = 'delete_file'
      }

      // Add the tool call response (assistant message with tool_calls)
      apiMessages.push({
        role: 'assistant',
        content: lastAssistantMessage.content,
        tool_calls: [{
          id: pendingFileToolCall.id,
          type: 'function',
          function: {
            name: toolName,
            arguments: JSON.stringify(pendingFileToolCall.args),
          },
        }],
      })

      // Add the tool result
      apiMessages.push({
        role: 'tool',
        content: message,
        tool_call_id: pendingFileToolCall.id,
      })

      set({ pendingFileToolCall: null })

      const continuationId = `msg-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
      set((state) => ({
        messages: [
          ...state.messages,
          {
            id: continuationId,
            role: 'assistant',
            content: '',
            timestamp: Date.now(),
            isStreaming: true,
          },
        ],
      }))

      const streamResponse = await smartChatCompletion({
        model,
        messages: apiMessages,
        stream: true,
        onChunk: (chunk) => {
          const currentMessage = get().messages.find(m => m.id === continuationId)
          const currentContent = typeof currentMessage?.content === 'string' ? currentMessage.content : ''
          set((state) => ({
            messages: state.messages.map((m) =>
              m.id === continuationId
                ? { ...m, content: currentContent + chunk }
                : m
            ),
          }))
        },
      })

      set((state) => ({
        messages: state.messages.map((m) =>
          m.id === continuationId ? { ...m, isStreaming: false } : m
        ),
      }))

      console.log('[Chat] File tool result sent successfully, LLM continued conversation')

    } catch (error) {
      console.error('[Chat] Error sending file tool result:', error)
      set({ error: error instanceof Error ? error.message : 'Failed to send file tool result' })
    }
  },

  sendMemoryToolResult: async (success: boolean, message: string) => {
    const { pendingMemoryToolCall } = get()
    if (!pendingMemoryToolCall) {
      console.warn('[Chat] No pending memory tool call to send result for')
      return
    }

    console.log('[Chat] Sending memory tool result:', { success, message })

    // Clear the pending tool call
    set({ pendingMemoryToolCall: null })

    // Memory tool doesn't need to continue the conversation
    // The result is already shown in the UI by App.tsx
  },
}))
